import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Ticket, QrCode, Gift, Trophy, Building2 } from "lucide-react";
import Countdown from "@/components/Countdown";
import Seo, { eventJsonLd, howToJsonLd, faqJsonLd } from "@/components/Seo";
import api from "@/lib/api";
import { AddressLink } from "@/components/ContactLinks";
import { resolveImgSrc } from "@/components/ImageUpload";

// Higher-quality hero — sizes queried by srcset so mobile gets a wider image
// (portrait crop pulls ~30% of the source, so we need extra resolution).
const HERO_BASE = "https://images.unsplash.com/photo-1558151748-f2621b5e52f0?crop=entropy&cs=srgb&fm=jpg";
const HERO = `${HERO_BASE}&w=2400&q=88`;
const HERO_SRCSET = [
  `${HERO_BASE}&w=1200&q=88 1200w`,
  `${HERO_BASE}&w=1800&q=88 1800w`,
  `${HERO_BASE}&w=2400&q=88 2400w`,
  `${HERO_BASE}&w=3200&q=85 3200w`,
].join(", ");
const CANCUN = "https://images.unsplash.com/photo-1762177390615-829cd3ef7c7c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const HOME_FAQ = [
  { q: "How do I enter ADN Local FanFest?",
    a: "Register free at /register with your name, email, phone, ZIP, and date of birth. It takes about 60 seconds. Must be 18 or older, one entry per family/household, no purchase necessary." },
  { q: "How much does it cost to enter?",
    a: "Nothing. Entry is completely free and no purchase is necessary. A purchase will not increase your chances of winning." },
  { q: "What is the Grand Prize?",
    a: "6 days / 5 nights in Cancun for two — roundtrip airfare included (about $800/person) plus a $500 cash award for the winner. Winner pays only nightly taxes and resort fees. Total ARV $2,999. Drawn live after the World Cup Final on July 19, 2026." },
  { q: "How do I qualify for the Grand Prize?",
    a: "Register, then check in via the ADN app at any Official Watch Party Partner in Jacksonville between 12:00 PM and 2:45 PM EDT on July 19, 2026." },
  { q: "When are daily prizes drawn?",
    a: "Daily prizes are drawn every day from July 6 through July 18, 2026. You do not have to be present to win a daily prize." },
];

export default function Home() {
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => { api.get("/businesses?watch_party_only=true").then(r => setSponsors(r.data.slice(0,6))); }, []);
  return (
    <div>
      <Seo
        title="Watch. Check In. Win."
        description="Register free for ADN Local FanFest — Jacksonville's 2026 World Cup watch-party sweepstakes. Check in at local Watch Party Partners and win daily prizes plus a Cancun Grand Prize. No purchase necessary."
        jsonLd={[eventJsonLd(), howToJsonLd(), faqJsonLd(HOME_FAQ)]}
        keywords="Jacksonville watch party, World Cup 2026 Jacksonville, ADN Local FanFest, Cancun sweepstakes, free sweepstakes Jacksonville"
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] w-full overflow-hidden" aria-labelledby="hero-heading">
        <img
          src={HERO}
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt="Cheering crowd at a Jacksonville watch party during the 2026 World Cup"
          className="absolute inset-0 h-full w-full object-cover object-center will-change-transform"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-20 grid gap-10 md:grid-cols-2 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/40 bg-primary/10 text-primary text-xs tracking-[0.3em] uppercase">
              Jacksonville Edition · Summer 2026
            </div>
            <h1 id="hero-heading" className="mt-6 font-bebas text-5xl sm:text-6xl lg:text-7xl leading-none tracking-wider">
              WATCH. <span className="green-text">CHECK IN.</span> <span className="gold-text">WIN.™</span>
            </h1>
            <p className="mt-5 text-white/80 max-w-lg text-base sm:text-lg leading-relaxed">
              Register free, check in at Official Watch Party Partners™ across Jacksonville, and unlock a shot at the <span className="gold-text font-semibold">Cancun Grand Prize</span> — drawn live after the July 19, 2026 World Cup Final.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-register-btn"
                className="inline-flex items-center gap-2 px-6 py-4 bg-primary text-black font-bebas text-2xl tracking-wider rounded-sm hover:brightness-110 min-h-[56px]">
                ENTER FREE <ArrowRight size={18}/>
              </Link>
              <Link to="/fanfest" data-testid="hero-learn-btn"
                className="inline-flex items-center gap-2 px-6 py-4 border border-white/25 text-white font-bebas text-2xl tracking-wider rounded-sm hover:border-primary hover:text-primary min-h-[56px]">
                HOW IT WORKS
              </Link>
            </div>
            <p className="mt-4 text-xs text-white/50 max-w-md">No purchase necessary. One entry per family/household. Must be 18+.</p>
          </div>
          <div className="justify-self-center md:justify-self-end">
            <div className="adn-card p-6 max-w-sm">
              <div className="text-[10px] tracking-[0.3em] uppercase text-primary/80 mb-3">Kickoff Countdown</div>
              <Countdown />
              <div className="mt-4 text-center text-xs text-white/60">World Cup Final · July 19, 2026 · 3:00 PM EDT</div>
            </div>
          </div>
        </div>
      </section>

      {/* Grand Prize */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-12 relative z-10">
        <div className="grand-card overflow-hidden grid md:grid-cols-2">
              <img src={CANCUN} className="h-64 md:h-full w-full object-cover"
                   alt="Aerial view of Cancun beach — the ADN FanFest Grand Prize destination"
                   loading="lazy" />
          <div className="p-8 sm:p-10">
            <div className="text-[10px] tracking-[0.3em] uppercase purple-text mb-3">Grand Prize</div>
            <h2 className="font-bebas text-4xl sm:text-5xl gold-text leading-none">
              CANCUN 6 DAYS / 5 NIGHTS + $500 CASH
            </h2>
            <p className="mt-4 text-white/80 leading-relaxed">
              6 days / 5 nights in Cancun for two, roundtrip airfare included, plus $500 cash for the winner. Winner covers only nightly taxes and resort fees. ARV $2,999. Drawn live after the World Cup Final on July 19, 2026.
              Must be checked in at a participating Watch Party Partner™ between 12:00 – 2:45 PM EDT to qualify.
            </p>
            <Link to="/prizes" data-testid="grand-prize-details-btn"
              className="mt-6 inline-flex items-center gap-2 text-primary font-semibold hover:underline">
              See all prizes <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-bebas text-4xl sm:text-5xl">HOW IT WORKS</h2>
        <p className="text-white/60 mt-2 text-sm tracking-widest uppercase">Three steps to win</p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { icon: Ticket, title: "01 · REGISTER FREE", body: "One entry per family/household. Takes 60 seconds. Must be 18+." },
            { icon: QrCode, title: "02 · CHECK IN & STAMP", body: "Scan a QR at any Official Watch Party Partner™. Collect Fan Passport™ stamps." },
            { icon: Gift, title: "03 · WIN DAILY & GRAND", body: "13 daily prizes July 6–18. Final Day: Cancun Grand Prize drawn live." },
          ].map((s,i)=>(
            <div key={i} className="adn-card adn-card-hover p-8">
              <div className="w-12 h-12 rounded bg-primary/15 text-primary flex items-center justify-center mb-4"><s.icon /></div>
              <h3 className="font-bebas text-2xl">{s.title}</h3>
              <p className="text-white/70 mt-2 text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sponsors */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-bebas text-4xl sm:text-5xl">OFFICIAL WATCH PARTY PARTNERS™</h2>
            <p className="text-white/60 mt-2 text-sm">Where Jacksonville gathers to cheer.</p>
          </div>
          <Link to="/directory" data-testid="see-all-partners" className="hidden sm:inline-flex items-center gap-1 text-primary text-sm">See all <ArrowRight size={14}/></Link>
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sponsors.map(b => (
            <Link key={b.id} to={`/business/${b.id}`}
              data-testid={`home-sponsor-${b.id}`}
              className="adn-card adn-card-hover overflow-hidden group">
              <div className="h-40 overflow-hidden">
                <img src={resolveImgSrc(b.photos && b.photos[0]) || "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800"}
                     alt={`${b.business_name} — ${(b.categories || []).join(", ")} in Jacksonville`}
                     loading="lazy"
                     className="w-full h-full object-cover group-hover:scale-105 transition"/>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase">
                  <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary">{(b.sponsor_tier||"").toUpperCase()}</span>
                  {b.watch_party_partner && <span className="px-2 py-0.5 rounded-full bg-secondary/20 text-secondary">Watch Party</span>}
                </div>
                <div className="font-bebas text-2xl mt-2">{b.business_name}</div>
                <div className="text-xs text-white/60 mt-1 flex items-center gap-1" onClick={(e)=>e.stopPropagation()}>
                  <MapPin size={12}/>
                  <AddressLink address={b.address} className="hover:text-primary transition-colors">{b.address}</AddressLink>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Business CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="adn-card p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="w-14 h-14 rounded bg-secondary/20 text-secondary flex items-center justify-center">
            <Building2 />
          </div>
          <div className="flex-1">
            <h3 className="font-bebas text-3xl sm:text-4xl">OWN A LOCAL BUSINESS?</h3>
            <p className="text-white/70 mt-2">Become a Watch Party Partner™. Get discovered, tracked check-ins, and city-wide promotion.</p>
          </div>
          <Link to="/sponsors" data-testid="business-cta"
            className="px-6 py-4 bg-secondary text-black font-bebas text-xl rounded-sm hover:brightness-110">
            BECOME A SPONSOR
          </Link>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24" aria-labelledby="faq-heading">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-primary">Common Questions</div>
            <h2 id="faq-heading" className="font-bebas text-4xl sm:text-5xl mt-2">FANFEST FAQ</h2>
          </div>
          <Link to="/faq" data-testid="home-see-all-faq" className="hidden sm:inline-flex items-center gap-1 text-primary text-sm">
            See all <ArrowRight size={14}/>
          </Link>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {HOME_FAQ.map((f, i) => (
            <details key={i} className="adn-card p-5 group" data-testid={`home-faq-${i}`}>
              <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
                <h3 className="font-bebas text-xl">{f.q}</h3>
                <ArrowRight size={16} className="mt-1 text-primary shrink-0 group-open:rotate-90 transition"/>
              </summary>
              <p className="mt-3 text-sm text-white/80 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="sm:hidden mt-6 text-center">
          <Link to="/faq" className="text-primary underline">See all FAQ →</Link>
        </div>
      </section>
    </div>
  );
}
